import { Reveal } from "./Reveal";
import { InkDivider } from "./InkDivider";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export const FeaturedProjects = ({ projects = [], categories = [], filter = "All", setFilter }) => {
  const catsOf = (p) => (p.categories && p.categories.length ? p.categories : [p.category]);
  const shown = filter === "All" ? projects : projects.filter((p) => catsOf(p).includes(filter));
  const projectCategoryNames = [...new Set(projects.flatMap(catsOf))];
  const activeCategories = categories.filter((c) => projectCategoryNames.includes(c.name));
  const chips = [{ name: "All", slug: "all" }, ...activeCategories.map((c) => ({ name: c.name, slug: c.slug }))];
  const showFilters = activeCategories.length > 1;

  return (
    <section id="work" data-testid="featured-section" className="relative bg-[#FFFFFF] py-24 sm:py-36">
      <InkDivider color="#6B8F71" className="absolute top-0 left-0 -translate-y-1/2" />
      <div className="max-w-6xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="font-sans text-xs tracking-[0.32em] uppercase text-[#C9A24B]">
            03 · Selected work
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-ink mt-4">
            {filter === "All" ? "A few things I've shaped." : filter}
          </h2>
        </Reveal>

        {/* discipline filter chips */}
        {showFilters && (
          <Reveal delay={0.06}>
            <div className="mt-8 flex flex-wrap items-center gap-2.5" data-testid="work-filters">
              {chips.map((chip) => {
                const active = filter === chip.name;
                return (
                  <button
                    key={chip.slug}
                    type="button"
                    data-testid={`work-filter-${chip.slug}`}
                    onClick={() => setFilter && setFilter(chip.name)}
                    style={{
                      backgroundColor: active ? "#1A1A1A" : "transparent",
                      color: active ? "#FFFFFF" : "#1A1A1A",
                      borderColor: active ? "#1A1A1A" : "rgba(26,26,26,0.25)",
                    }}
                    className="font-sans text-sm sm:text-base whitespace-nowrap leading-none px-5 py-3 rounded-full border transition-colors hover:border-[#1A1A1A]"
                  >
                    {chip.name}
                  </button>
                );
              })}
            </div>
          </Reveal>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-10">
          {shown.map((p, i) => {
            const accent = p.accent || "#6B8F71";
            return (
              <Reveal key={p.slug} delay={(i % 3) * 0.08}>
                <Link
                  to={`/work/${p.slug}`}
                  data-testid={`project-card-${p.slug}`}
                  className="block group h-full bg-[#FFFFFF] rounded-2xl overflow-hidden border border-[#1A1A1A]/10 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_22px_50px_-28px_rgba(26,26,26,0.45)]"
                  style={{ "--accent": accent }}
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-[#FFFFFF]">
                    {p.image ? (
                      <img
                        src={p.image}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                      />
                    ) : p.tool_url ? (
                      <div className="absolute inset-0 pointer-events-none bg-white">
                        <iframe
                          src={p.tool_url}
                          title={p.title}
                          loading="lazy"
                          tabIndex={-1}
                          aria-hidden="true"
                          className="origin-top-left border-0"
                          style={{ width: "200%", height: "200%", transform: "scale(0.5)" }}
                        />
                      </div>
                    ) : null}
                    <div className="absolute top-3 left-3 flex flex-wrap gap-1.5 max-w-[85%]">
                      {catsOf(p).map((cat) => (
                        <span
                          key={cat}
                          className="font-sans text-[11px] tracking-wide px-2.5 py-1 rounded-full bg-[#FFFFFF]/90 backdrop-blur border border-[#1A1A1A]/10"
                          style={{ color: p.accent || "#6B8F71" }}
                        >
                          {cat}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-2xl text-ink leading-snug">{p.title}</h3>
                    {p.tag && (
                      <p className="font-mono text-[11px] tracking-wide mt-1.5" style={{ color: accent }}>
                        {p.tag}
                      </p>
                    )}
                    <p className="font-sans text-sm text-ink/75 mt-2 leading-relaxed">
                      {p.description}
                    </p>
                    <span
                      className="mt-5 inline-flex items-center gap-1.5 font-sans text-sm opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-500"
                      style={{ color: accent }}
                    >
                      View project <ArrowRight size={15} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProjects;
